// Last-modified strip rendered under the dialog title on the Tagging
// Rule + File Workflow edit dialogs (FR #194 + #195). Surfaces
// `createdAt` + `updatedAt` so the operator can answer "when was this
// last touched?" without leaving the edit view.
//
// v1: simple two-row strip. v2 (when audit-log click-through ships
// per FR #198 — CP value provenance) will turn the updated stamp
// into a link to the activity log filtered to this entity.

export interface EditedTimestampStripProps {
  createdAt: string;
  updatedAt: string;
}

function formatAbsolute(iso: string): string {
  // Locale string is fine here — operators are reading this in-app
  // and a 24h vs 12h preference is handled by the OS.
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatRelative(iso: string, now = Date.now()): string {
  let dt: number;
  try {
    dt = new Date(iso).getTime();
  } catch {
    return '';
  }
  if (Number.isNaN(dt)) return '';
  const seconds = Math.max(0, Math.round((now - dt) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.round(months / 12);
  return `${years}y ago`;
}

export function EditedTimestampStrip({ createdAt, updatedAt }: EditedTimestampStripProps) {
  const updatedRel = formatRelative(updatedAt);
  const updatedAbs = formatAbsolute(updatedAt);
  const createdAbs = formatAbsolute(createdAt);

  return (
    <div
      style={{
        fontSize: 11,
        color: 'var(--text-muted)',
        marginTop: -8,
        marginBottom: 16,
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span title={updatedAbs}>
        <strong style={{ fontWeight: 600 }}>Updated:</strong> {updatedRel} <span style={{ opacity: 0.7 }}>({updatedAbs})</span>
      </span>
      <span title={createdAbs}>
        <strong style={{ fontWeight: 600 }}>Created:</strong> {createdAbs}
      </span>
    </div>
  );
}
